/* ez-predictive-search.js — drives the search overlay (snippets/ez-predictive-search.liquid,
   rendered once from layout/theme.liquid).

   Opened by any [data-ez-search-open] click (the header emits it) or the `ez:search:open` event.
   Debounced input queries /search/suggest.json for products, articles and pages; groups results,
   supports up/down/enter keyboard nav across all hits + the "view all" link. ESC closes,
   focus-trapped, reduced-motion aware (CSS). Bespoke lean markup; API shape per Horizon. */
(function () {
  'use strict';

  var DEBOUNCE = 220;
  var LIMIT = 4; // per resource type
  var FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),[tabindex]:not([tabindex="-1"])';

  var root, panel, input, form, results, hint, loadingEl, emptyEl, viewAll, skirtEl;
  var groups = {}, lists = {}, productTpl, textTpl;
  var lastFocus = null;
  var timer = null;
  var activeIndex = -1;
  var lastCtl = null;

  function q(sel, r) { return (r || document).querySelector(sel); }

  // /search/suggest.json returns titles verbatim; migrated products carry a literal
  // "&amp;" in the stored title (WooCommerce export artifact). Liquid pages decode it
  // in-browser; text-node rendering here does not — so decode once, matching the parser.
  var _decoder = null;
  function decodeEntities(str) {
    if (!str) return '';
    if (str.indexOf('&') === -1) return str;
    _decoder = _decoder || document.createElement('textarea');
    _decoder.innerHTML = str;
    return _decoder.value;
  }

  // suggest.json `price` is a bare decimal string in the presentment currency ("24.99", "36.00").
  // Format it like the search-results cards (snippets/ez-money): USD = the store money format
  // ("$24.99", "$99.00"); any other currency = window.EZ.money ("CA$36"), never a bare "$".
  function priceText(raw) {
    if (raw == null || raw === '') return '';
    var n = parseFloat(String(raw).replace(/,/g, ''));
    if (!isFinite(n)) return String(raw);
    var E = window.EZ;
    if (E && E.isBase === false && typeof E.money === 'function') return E.money(n);
    return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  function cache() {
    root = q('[data-ez-search]');
    if (!root) return false;
    panel = q('[data-ez-search-panel]', root);
    input = q('[data-ez-search-input]', root);
    form = q('[data-ez-search-form]', root);
    results = q('[data-ez-search-results]', root);
    hint = q('[data-ez-search-hint]', root);
    loadingEl = q('[data-ez-search-loading]', root);
    emptyEl = q('[data-ez-search-empty]', root);
    viewAll = q('[data-ez-search-viewall]', root);
    skirtEl = q('[data-ez-search-skirt]', root);
    ['products', 'articles', 'pages'].forEach(function (t) {
      groups[t] = q('[data-ez-search-group="' + t + '"]', root);
      lists[t] = q('[data-ez-search-list="' + t + '"]', root);
    });
    productTpl = q('[data-ez-search-product-tpl]', root);
    textTpl = q('[data-ez-search-text-tpl]', root);
    return true;
  }

  /* ---------- query ---------- */

  // Hardware-intent boost: shoppers reach for the snap hardware (Snap Pack, Stud Pack)
  // by many words — "fasteners", "snaps", "studs", "caps", "pins". Bare "snap"/"stud"
  // floods (the brand name is "EZ-Snap"; kit descriptions mention studs), and "snaps"
  // buries the Stud Pack. "fastener" matches ONLY the hardware, so when an intent word
  // is typed we merge the clean "fastener" hits to the front. Auto-tracks the catalog —
  // no SKUs hardcoded — so a future fastener product surfaces the moment it ships.
  var HARDWARE_INTENT = ['snap', 'snaps', 'stud', 'studs', 'cap', 'caps', 'pin', 'pins', 'socket', 'sockets'];
  var HARDWARE_CANON = 'fastener';

  function wantsHardwareBoost(term) {
    var lc = term.toLowerCase().trim();
    if (!lc || lc.indexOf('fastener') !== -1) return false; // "fastener(s)" already returns the hardware
    return lc.split(/\s+/).some(function (t) { return HARDWARE_INTENT.indexOf(t) !== -1; });
  }

  // Skirting is a CONFIGURED product (pick your RV length → the builder sizes the kit), so a "skirt(ing)"
  // query leads with the pinned builder + collection + size chips (static block in the liquid) instead of
  // a scattered subset of the 7 near-identical band PDPs.
  var SKIRT_INTENT = /skirt/i;
  function wantsSkirtFeatured(term) { return SKIRT_INTENT.test(term || ''); }

  function suggestUrl(term) {
    var params = new URLSearchParams();
    params.set('q', term);
    params.append('resources[type]', 'product,article,page');
    params.set('resources[limit]', String(LIMIT));
    params.set('resources[options][unavailable_products]', 'last');
    params.set('resources[options][fields]', 'title,product_type,variants.title,vendor,body');
    return '/search/suggest.json?' + params.toString();
  }

  function onInput() {
    var term = input.value.trim();
    clearTimeout(timer);
    if (!term) { showState('hint'); return; }
    timer = setTimeout(function () { runSearch(term); }, DEBOUNCE);
  }

  function runSearch(term) {
    if (lastCtl) lastCtl.abort();
    lastCtl = ('AbortController' in window) ? new AbortController() : null;
    showState('loading');
    var sig = lastCtl ? lastCtl.signal : undefined;
    var get = function (t) {
      return fetch(suggestUrl(t), { headers: { Accept: 'application/json' }, signal: sig })
        .then(function (r) { return r.json(); });
    };

    var boost = wantsHardwareBoost(term);
    var jobs = [get(term)];
    if (boost) jobs.push(get(HARDWARE_CANON).catch(function () { return null; }));

    Promise.all(jobs)
      .then(function (arr) {
        var res = (arr[0] && arr[0].resources && arr[0].resources.results) || {};
        if (boost && arr[1]) {
          var extra = (arr[1].resources && arr[1].resources.results && arr[1].resources.results.products) || [];
          var base = res.products || [];
          var seen = {};
          base.forEach(function (p) { seen[p.id] = 1; });
          // pin canonical hardware to the front, deduped against the primary results
          res.products = extra.filter(function (p) { return !seen[p.id]; }).concat(base);
        }
        renderResults(res, term);
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        showState('empty', 'Something went wrong. Press enter to search the full site.');
      });
  }

  /* ---------- render ---------- */

  function showState(state, msg) {
    if (hint) hint.hidden = state !== 'hint';
    if (loadingEl) loadingEl.hidden = state !== 'loading';
    if (emptyEl) { emptyEl.hidden = state !== 'empty'; if (msg) emptyEl.textContent = msg; }
    if (state !== 'results') {
      Object.keys(groups).forEach(function (t) { if (groups[t]) groups[t].hidden = true; });
      if (skirtEl) skirtEl.hidden = true;
      if (viewAll) viewAll.hidden = true;
      activeIndex = -1;
      if (input) input.setAttribute('aria-expanded', 'false');
    }
  }

  function highlight(text, term) {
    var span = document.createElement('span');
    if (!term) { span.textContent = text; return span; }
    var lc = text.toLowerCase(), lt = term.toLowerCase(), idx = lc.indexOf(lt);
    if (idx < 0) { span.textContent = text; return span; }
    span.appendChild(document.createTextNode(text.slice(0, idx)));
    var mark = document.createElement('mark');
    mark.textContent = text.slice(idx, idx + term.length);
    span.appendChild(mark);
    span.appendChild(document.createTextNode(text.slice(idx + term.length)));
    return span;
  }

  function productImg(p) {
    var src = p.image || p.featured_image;
    if (src && typeof src === 'object') src = src.url || src.src;
    if (!src && p.featured_image && p.featured_image.url) src = p.featured_image.url;
    return src || '';
  }

  function renderResults(res, term) {
    var skirt = wantsSkirtFeatured(term);
    if (skirtEl) skirtEl.hidden = !skirt; // pin the builder + collection + size chips for skirting queries
    if (skirt) {
      // the 7 band sizes are the pinned chips — drop them from the products group so they aren't
      // duplicated; the products group then carries skirting ACCESSORIES (snaps, studs, clamps, bag).
      // NB: suggest.json's `fields` option only affects MATCHING, not the returned payload, so
      // product_type isn't on the result — identify a band by its handle (rv-skirting-kit-<size>).
      res.products = (res.products || []).filter(function (p) { return !(p.url && /rv-skirting-kit-\d/.test(p.url)); });
    }
    var total = 0;

    var map = { products: res.products || [], articles: res.articles || [], pages: res.pages || [] };
    Object.keys(map).forEach(function (t) {
      var items = map[t];
      var list = lists[t], group = groups[t];
      if (!list || !group) return;
      list.innerHTML = '';
      if (!items.length) { group.hidden = true; return; }
      group.hidden = false;

      items.forEach(function (item) {
        var isProduct = t === 'products';
        var node = (isProduct ? productTpl : textTpl).content.firstElementChild.cloneNode(true);
        var link = q('[data-hit-link]', node);
        if (link) link.setAttribute('href', item.url || '#');

        var title = decodeEntities(item.title || '');
        if (window.EZ && typeof window.EZ.usd === 'function') title = window.EZ.usd(title);
        var titleEl = q('[data-hit-title]', node);
        if (titleEl) { titleEl.textContent = ''; titleEl.appendChild(highlight(title, term)); }

        if (isProduct) {
          var img = q('[data-hit-img]', node);
          var src = productImg(item);
          if (img) {
            if (src) { img.src = src; img.alt = title; }
            else { var m = q('.ez-search-hit__media', node); if (m) m.remove(); }
          }
          var priceEl = q('[data-hit-price]', node);
          if (priceEl) priceEl.textContent = priceText(item.price);
        }
        list.appendChild(node);
        total += 1;
      });
    });

    // the pinned skirting block is real content even when no products/articles/pages matched
    var hasFeatured = skirt && skirtEl && !skirtEl.hidden;
    if (!total && !hasFeatured) { showState('empty', 'No matches for “' + term + '”. Press enter to search everything.'); return; }

    if (hint) hint.hidden = true;
    if (loadingEl) loadingEl.hidden = true;
    if (emptyEl) emptyEl.hidden = true;
    if (viewAll) {
      viewAll.hidden = false;
      viewAll.setAttribute('href', '/search?q=' + encodeURIComponent(term));
    }
    if (input) input.setAttribute('aria-expanded', 'true');
    activeIndex = -1;
  }

  /* ---------- keyboard nav ---------- */

  function navItems() {
    var hits = Array.prototype.slice.call(root.querySelectorAll('.ez-search-hit:not([hidden])'))
      .filter(function (li) { return li.closest('.ez-search__group') && !li.closest('.ez-search__group').hidden; });
    if (viewAll && !viewAll.hidden) hits.push(viewAll);
    return hits;
  }

  function setActive(items, idx) {
    items.forEach(function (el) { el.classList.remove('is-active'); });
    activeIndex = idx;
    if (idx < 0 || idx >= items.length) return;
    var el = items[idx];
    el.classList.add('is-active');
    var scroller = results;
    var link = el.querySelector('[data-hit-link]') || el;
    if (link.scrollIntoView) link.scrollIntoView({ block: 'nearest' });
  }

  function onKeydown(e) {
    if (e.key === 'Escape' || e.key === 'Esc') { e.preventDefault(); close(); return; }

    if (e.key === 'Tab') {
      var nodes = Array.prototype.slice.call(panel.querySelectorAll(FOCUSABLE))
        .filter(function (n) { return n.offsetParent !== null && !n.hidden; });
      if (!nodes.length) return;
      var first = nodes[0], last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      return;
    }

    var items = navItems();
    if (e.key === 'ArrowDown') {
      if (!items.length) return;
      e.preventDefault();
      setActive(items, (activeIndex + 1) % items.length);
    } else if (e.key === 'ArrowUp') {
      if (!items.length) return;
      e.preventDefault();
      setActive(items, (activeIndex - 1 + items.length) % items.length);
    } else if (e.key === 'Enter') {
      if (activeIndex >= 0 && items[activeIndex]) {
        e.preventDefault();
        var link = items[activeIndex].querySelector('[data-hit-link]') || items[activeIndex];
        if (link && link.getAttribute('href')) window.location.href = link.getAttribute('href');
      }
      // else: let the form submit to /search?q=
    }
  }

  /* ---------- open / close ---------- */

  function open() {
    if (!root) return;
    lastFocus = document.activeElement;
    root.classList.add('is-open');
    root.setAttribute('aria-hidden', 'false');
    document.documentElement.style.overflow = 'hidden';
    setTimeout(function () { if (input) { input.focus(); input.select(); } }, 60);
    if (input && input.value.trim()) runSearch(input.value.trim());
    else showState('hint');
  }

  function close() {
    if (!root) return;
    root.classList.remove('is-open');
    root.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = '';
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  function init() {
    if (!cache()) return;

    document.addEventListener('click', function (e) {
      var opener = e.target.closest('[data-ez-search-open]');
      if (!opener) return;
      e.preventDefault();
      open();
    });
    document.addEventListener('ez:search:open', open);

    root.addEventListener('click', function (e) {
      if (e.target.closest('[data-ez-search-close]')) { e.preventDefault(); close(); }
    });

    if (input) input.addEventListener('input', onInput);
    if (panel) panel.addEventListener('keydown', onKeydown);

    // Form submit falls through to /search?q= for the full results page.
    if (form) form.addEventListener('submit', function () { /* native GET to routes.search_url */ });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
